import React, { useState, useEffect } from 'react';
import { 
  HelpCircle, 
  Briefcase, 
  Calendar, 
  Users, 
  Shield,
  ChevronDown,
  ChevronRight,
  Search,
  BookOpen,
  Filter,
  X,
  Sparkles,
  Activity,
  RefreshCw,
  ArrowLeft
} from 'lucide-react';
import { getFAQs, getFAQCategories, cleanupFAQs } from '../../services/faqService';
import { FAQ, FAQCategory } from '../../types';
import { useAuthStore } from '../../stores/authStore';
import { initializeDefaultFAQs } from '../../utils/initializeFAQs';
import { useNavigate } from 'react-router-dom';

const FAQPage: React.FC = () => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [categories, setCategories] = useState<FAQCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [expandedFaqs, setExpandedFaqs] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { userProfile, user } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    loadFAQs();
  }, []);

  // Chiudi dropdown quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showFilters && !target.closest('.filter-dropdown')) {
        setShowFilters(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilters]);

  const initializeFAQs = async () => {
    if (!user?.uid) return;
    
    try {
      setLoading(true);
      await initializeDefaultFAQs(user.uid);
      await loadFAQs();
    } catch (error) {
      console.error('Error initializing FAQs:', error);
      setError('Errore nell\'inizializzazione delle FAQ');
    } finally {
      setLoading(false);
    }
  };

  const loadFAQs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [faqsData, categoriesData] = await Promise.all([
        getFAQs(),
        getFAQCategories()
      ]);
      
      setFaqs(faqsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading FAQs:', error);
      // Se non ci sono categorie, mostra un messaggio specifico
      if (error instanceof Error && error.message.includes('categorie')) {
        setError('Le categorie FAQ non sono state inizializzate. Contatta un amministratore.');
      } else {
        setError('Errore nel caricamento delle FAQ. Riprova più tardi.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadFAQs();
    setIsRefreshing(false);
  };

  const toggleFAQ = (faqId: string) => {
    const newExpanded = new Set(expandedFaqs);
    if (newExpanded.has(faqId)) {
      newExpanded.delete(faqId);
    } else {
      newExpanded.add(faqId);
    }
    setExpandedFaqs(newExpanded);
  };

  const getCategoryIcon = (iconName: string) => {
    const icons: { [key: string]: React.ReactNode } = {
      HelpCircle: <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5" />,
      Briefcase: <Briefcase className="w-4 h-4 sm:w-5 sm:h-5" />,
      Calendar: <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />,
      Users: <Users className="w-4 h-4 sm:w-5 sm:h-5" />,
      Shield: <Shield className="w-4 h-4 sm:w-5 sm:h-5" />
    };
    return icons[iconName] || <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5" />;
  };

  const filteredFAQs = faqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryName = (category: string) => {
    const cat = categories.find(c => c.id === category);
    return cat?.name || category;
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSelectedCategory('all');
  };

  const handleCleanupFAQs = async () => {
    if (!user?.uid) return;
    
    try {
      setLoading(true);
      const result = await cleanupFAQs();
      console.log('🧹 Pulizia FAQ completata:', result);
      
      // Ricarica le FAQ dopo la pulizia
      await loadFAQs();
      
      // Mostra notifica di successo
      alert(`Pulizia completata! Rimossi ${result.duplicatesRemoved} duplicati, ${result.orderingFixed} ordini corretti, ${result.totalFAQs} FAQ totali.`);
    } catch (error) {
      console.error('❌ Errore durante la pulizia FAQ:', error);
      alert('Errore durante la pulizia delle FAQ. Controlla la console per i dettagli.');
    } finally {
      setLoading(false);
    }
  };

  // Funzione temporanea per eliminare categorie duplicate
  const handleCleanupCategories = async () => {
    if (!user?.uid) return;
    
    try {
      setLoading(true);
      
      // Ottieni tutte le categorie
      const allCategories = await getFAQCategories();
      console.log('📋 Tutte le categorie:', allCategories);
      
      // Trova duplicati per nome
      const categoryMap = new Map<string, FAQCategory>();
      const duplicates: FAQCategory[] = [];
      
      allCategories.forEach(category => {
        const normalizedName = category.name.toLowerCase().trim();
        if (categoryMap.has(normalizedName)) {
          duplicates.push(category);
        } else {
          categoryMap.set(normalizedName, category);
        }
      });
      
      console.log('🔍 Categorie duplicate trovate:', duplicates);
      
      if (duplicates.length === 0) {
        alert('Nessuna categoria duplicata trovata!');
        return;
      }
      
      // Chiedi conferma
      const confirmDelete = window.confirm(
        `Trovate ${duplicates.length} categorie duplicate:\n` +
        duplicates.map(c => `- ${c.name} (ID: ${c.id})`).join('\n') +
        '\n\nVuoi eliminarle?'
      );
      
      if (confirmDelete) {
        // Qui dovresti implementare la logica per eliminare le categorie
        // Per ora mostriamo solo i duplicati
        alert(`Categorie duplicate da eliminare:\n${duplicates.map(c => c.name).join(', ')}\n\nElimina manualmente da Firebase Console.`);
      }
      
    } catch (error) {
      console.error('❌ Errore durante la pulizia categorie:', error);
      alert('Errore durante la pulizia delle categorie. Controlla la console per i dettagli.');
    } finally {
      setLoading(false);
    }
  };

  // Calcola statistiche
  const stats = {
    totalFAQs: faqs.length,
    totalCategories: categories.length,
    searchResults: filteredFAQs.length,
    expandedFAQs: expandedFaqs.size
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm w-full">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Caricamento FAQ...</h3>
          <p className="text-gray-600">Stiamo preparando le domande frequenti</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm w-full">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <HelpCircle className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Errore</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex gap-2 justify-center flex-wrap">
            <button 
              onClick={loadFAQs}
              className="w-full bg-blue-600 text-white rounded-xl py-3 px-4 font-medium hover:bg-blue-700 transition-colors"
            >
              Riprova
            </button>
            {userProfile?.role === 'admin' && (
              <button 
                onClick={initializeFAQs}
                className="w-full bg-green-600 text-white rounded-xl py-3 px-4 font-medium hover:bg-green-700 transition-colors"
              >
                Inizializza FAQ
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 lg:p-4 lg:pr-8 space-y-4 lg:space-y-6">
      {/* Header con Welcome e Azioni Rapide */}
      <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 lg:gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 lg:gap-3 mb-2">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-1.5 lg:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg lg:rounded-xl transition-colors"
              >
                <ArrowLeft className="w-5 h-5 lg:w-6 lg:h-6" />
              </button>
              <div className="p-1.5 lg:p-2 bg-blue-100 rounded-lg lg:rounded-xl">
                <BookOpen className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900">Domande Frequenti</h1>
                <p className="text-sm lg:text-base text-gray-600">Trova risposte alle domande più comuni su come utilizzare la piattaforma</p>
              </div>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 lg:gap-3">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex items-center px-3 py-2 lg:px-4 lg:py-2 bg-gray-100 text-gray-700 rounded-lg lg:rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50 text-sm lg:text-base"
            >
              <RefreshCw className={`w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Aggiorna
            </button>
            {userProfile?.role === 'admin' && (
              <button
                onClick={handleCleanupFAQs}
                disabled={loading}
                className="flex items-center px-3 py-2 lg:px-4 lg:py-2 bg-orange-600 text-white rounded-lg lg:rounded-xl hover:bg-orange-700 transition-colors disabled:opacity-50 text-sm lg:text-base"
              >
                <Sparkles className="w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2" />
                Pulisci
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Statistiche Principali - Mobile: 2x2, Desktop: 1x4 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Totale FAQ</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stats.totalFAQs}</p>
              <p className="text-xs text-gray-500 mt-1">Disponibili</p>
            </div>
            <div className="p-2 lg:p-3 bg-blue-100 rounded-lg lg:rounded-xl">
              <BookOpen className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Categorie</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stats.totalCategories}</p>
              <p className="text-xs text-gray-500 mt-1">Organizzate</p>
            </div>
            <div className="p-2 lg:p-3 bg-green-100 rounded-lg lg:rounded-xl">
              <Filter className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Risultati</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stats.searchResults}</p>
              <p className="text-xs text-gray-500 mt-1">Trovati</p>
            </div>
            <div className="p-2 lg:p-3 bg-yellow-100 rounded-lg lg:rounded-xl">
              <Search className="w-5 h-5 lg:w-6 lg:h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Aperte</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stats.expandedFAQs}</p>
              <p className="text-xs text-gray-500 mt-1">Visualizzate</p>
            </div>
            <div className="p-2 lg:p-3 bg-purple-100 rounded-lg lg:rounded-xl">
              <ChevronDown className="w-5 h-5 lg:w-6 lg:h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 lg:w-5 lg:h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Cerca nelle FAQ..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-12 py-3 lg:py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 focus:bg-white transition-all text-sm lg:text-base"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-4 h-4 lg:w-5 lg:h-5" />
                </button>
              )}
            </div>
          </div>
          <div className="text-sm lg:text-base text-gray-600">
            {filteredFAQs.length} di {faqs.length} FAQ
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6">
        {/* Mobile: Dropdown elegante */}
        <div className="lg:hidden filter-dropdown">
          <div className="relative">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Filter className="w-4 h-4 text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">
                    {selectedCategory === 'all' ? 'Tutte le categorie' : getCategoryName(selectedCategory)}
                  </p>
                  <p className="text-xs text-gray-500">Filtra per categoria</p>
                </div>
              </div>
              <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`} />
            </button>
            
            {showFilters && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-10 max-h-64 overflow-y-auto">
                <div className="p-2">
                  <button
                    onClick={() => {
                      setSelectedCategory('all');
                      setShowFilters(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                      selectedCategory === 'all'
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <div className={`p-1.5 rounded-lg ${
                      selectedCategory === 'all' ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      <HelpCircle className="w-4 h-4" />
                    </div>
                    <span className="font-medium">Tutte le categorie</span>
                    {selectedCategory === 'all' && (
                      <div className="ml-auto w-2 h-2 bg-blue-500 rounded-full"></div>
                    )}
                  </button>
                  
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => {
                        setSelectedCategory(category.id);
                        setShowFilters(false);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                        selectedCategory === category.id
                          ? 'bg-blue-50 text-blue-700 border border-blue-200'
                          : 'hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      <div className={`p-1.5 rounded-lg ${
                        selectedCategory === category.id ? 'bg-blue-100' : 'bg-gray-100'
                      }`}>
                        {getCategoryIcon(category.icon)}
                      </div>
                      <span className="font-medium">{category.name}</span>
                      {selectedCategory === category.id && (
                        <div className="ml-auto w-2 h-2 bg-blue-500 rounded-full"></div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Desktop: Filtri sempre visibili */}
        <div className="hidden lg:block">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`group relative px-4 lg:px-6 py-2 lg:py-3 rounded-xl text-sm font-semibold transition-all duration-300 flex items-center gap-2 lg:gap-3 shadow-lg hover:shadow-xl ${
                selectedCategory === 'all'
                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-blue-200 transform scale-105'
                  : 'bg-gray-100 text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 border border-gray-200 hover:border-blue-300'
              }`}
            >
              <div className={`p-1.5 lg:p-2 rounded-lg transition-all duration-300 ${
                selectedCategory === 'all'
                  ? 'bg-white/20'
                  : 'bg-white group-hover:bg-blue-100'
              }`}>
                <HelpCircle className="w-4 h-4 lg:w-5 lg:h-5" />
              </div>
              <span className="font-medium">Tutte</span>
              {selectedCategory === 'all' && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
              )}
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`group relative px-4 lg:px-6 py-2 lg:py-3 rounded-xl text-sm font-semibold transition-all duration-300 flex items-center gap-2 lg:gap-3 shadow-lg hover:shadow-xl ${
                  selectedCategory === category.id
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-blue-200 transform scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 border border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className={`p-1.5 lg:p-2 rounded-lg transition-all duration-300 ${
                  selectedCategory === category.id
                    ? 'bg-white/20'
                    : 'bg-white group-hover:bg-blue-100'
                }`}>
                  {getCategoryIcon(category.icon)}
                </div>
                <span className="font-medium">{category.name}</span>
                {selectedCategory === category.id && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ List */}
      <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg">
        <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-100 flex justify-between items-center">
          <h2 className="text-lg lg:text-xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600" />
            FAQ ({filteredFAQs.length})
          </h2>
          <div className="text-sm lg:text-base text-gray-600">
            Clicca per espandere
          </div>
        </div>
        
        <div className="p-4 lg:p-6">
          {filteredFAQs.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-500">
                <HelpCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">
                  {searchTerm 
                    ? 'Nessuna FAQ trovata'
                    : 'Nessuna FAQ disponibile'
                  }
                </p>
                <p className="text-sm">
                  {searchTerm 
                    ? `Nessuna FAQ corrisponde alla ricerca "${searchTerm}"`
                    : 'Non ci sono ancora FAQ disponibili per questa categoria'
                  }
                </p>
                {(searchTerm || selectedCategory !== 'all') && (
                  <button
                    onClick={clearSearch}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    Mostra tutte le FAQ
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3 lg:space-y-4">
              {filteredFAQs.map((faq, index) => (
                <div
                  key={faq.id}
                  className="bg-gray-50 rounded-xl p-4 lg:p-6 border border-gray-200 hover:shadow-lg transition-all duration-200 hover:border-gray-300"
                >
                  <button
                    onClick={() => toggleFAQ(faq.id)}
                    className="w-full text-left hover:bg-gray-100 rounded-lg p-3 transition-colors flex items-center justify-between group"
                  >
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="p-2 bg-blue-100 rounded-lg mt-1 flex-shrink-0">
                        {getCategoryIcon(categories.find(c => c.id === faq.category)?.icon || 'HelpCircle')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 text-left text-sm lg:text-base leading-relaxed group-hover:text-blue-600 transition-colors">
                          {faq.question}
                        </h3>
                        <p className="text-xs lg:text-sm text-gray-500 mt-1">
                          {getCategoryName(faq.category)}
                        </p>
                      </div>
                    </div>
                    <div className="flex-shrink-0 ml-3">
                      {expandedFaqs.has(faq.id) ? (
                        <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-all duration-200" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-all duration-200" />
                      )}
                    </div>
                  </button>
                  
                  {expandedFaqs.has(faq.id) && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="text-gray-700 leading-relaxed text-sm lg:text-base bg-white rounded-lg p-4 border border-gray-100">
                        {faq.answer}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 text-center">
        <div className="max-w-md mx-auto">
          <p className="text-gray-600 text-sm leading-relaxed">
            Non trovi la risposta che cerchi? 
            {userProfile?.role === 'admin' && (
              <span className="ml-1">
                <a href="/admin/faq" className="text-blue-600 hover:text-blue-700 font-medium transition-colors">
                  Gestisci le FAQ
                </a>
              </span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;